import React, { useState, useEffect, useCallback } from 'react';
import './GunCabinet.css';
import { Trash2, Lock, AlertCircle, Calendar, Droplet, Plus, X } from 'lucide-react';
import { equipmentService } from '../../../../../services';

const GunCabinet = () => {
    const [activeTab, setActiveTab] = useState('certificates'); // 'certificates', 'ammo', 'services'
    const [certificates, setCertificates] = useState([]);
    const [ammoLogs, setAmmoLogs] = useState([]);
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Modal states
    const [showCertModal, setShowCertModal] = useState(false);
    const [showAmmoModal, setShowAmmoModal] = useState(false);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [equipment, setEquipment] = useState([]);
    
    // Form states
    const [certForm, setCertForm] = useState({
        certificateType: 'SGC',
        certificateNumber: '',
        issuedDate: '',
        expiryDate: '',
        issuingAuthority: '',
        totalSlots: 0,
        notes: ''
    });
    
    const [ammoForm, setAmmoForm] = useState({
        calibre: '',
        transactionType: 'Purchased',
        quantity: 0,
        description: '',
        source: '',
        cost: '',
        transactionDate: new Date().toISOString().split('T')[0],
        notes: '',
        equipmentId: ''
    });
    
    const [serviceForm, setServiceForm] = useState({
        equipmentId: '',
        serviceType: 'AnnualService',
        serviceDate: new Date().toISOString().split('T')[0],
        provider: '',
        cost: '',
        workDone: '',
        nextServiceDue: '',
        notes: ''
    });

    const fetchEquipment = useCallback(async () => {
        try {
            const data = await equipmentService.getUserFirearms();
            setEquipment(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching equipment:', err);
        }
    }, []);
    
    const fetchCertificates = useCallback(async () => {
        try {
            const data = await equipmentService.getFirearmCertificates();
            setCertificates(data);
        } catch (err) {
            console.error('Error fetching certificates:', err);
        }
    }, []);

    const fetchAmmoLogs = useCallback(async () => {
        try {
            const data = await equipmentService.getAmmunitionLogs();
            setAmmoLogs(data);
        } catch (err) {
            console.error('Error fetching ammo logs:', err);
        }
    }, []);

    const fetchServices = useCallback(async () => {
        try {
            const data = await equipmentService.getEquipmentServices();
            setServices(data);
        } catch (err) {
            console.error('Error fetching services:', err);
        }
    }, []);

    useEffect(() => {
        fetchEquipment();
    }, [fetchEquipment]);
    
    useEffect(() => {
        setIsLoading(true);
        if (activeTab === 'certificates') {
            fetchCertificates().finally(() => setIsLoading(false));
        } else if (activeTab === 'ammo') {
            fetchAmmoLogs().finally(() => setIsLoading(false));
        } else if (activeTab === 'services') {
            fetchServices().finally(() => setIsLoading(false));
        }
    }, [activeTab, fetchCertificates, fetchAmmoLogs, fetchServices]);
    
    // Create certificate
    const handleCreateCertificate = async (e) => {
        e.preventDefault();
        try {
            await equipmentService.createFirearmCertificate({
                certificateType: certForm.certificateType,
                certificateNumber: certForm.certificateNumber,
                issueDate: certForm.issuedDate,
                expiryDate: certForm.expiryDate,
                issuingConstabulary: certForm.issuingAuthority,
                slotsGranted: parseInt(certForm.totalSlots, 10) || 0,
                notes: certForm.notes
            });
            await fetchCertificates();
            setShowCertModal(false);
            resetCertForm();
        } catch (err) {
            console.error('Error creating certificate:', err);
            setError('Failed to create certificate.');
        }
    };
    
    // Create ammo log
    const handleCreateAmmoLog = async (e) => {
        e.preventDefault();
        try {
            await equipmentService.createAmmunitionLog({
                calibre: ammoForm.calibre,
                transactionType: ammoForm.transactionType,
                quantity: ammoForm.transactionType === 'Used' ? -Math.abs(parseInt(ammoForm.quantity, 10)) : Math.abs(parseInt(ammoForm.quantity, 10)),
                description: ammoForm.description,
                source: ammoForm.source,
                cost: ammoForm.cost ? parseFloat(ammoForm.cost) : null,
                transactionDate: ammoForm.transactionDate,
                notes: ammoForm.notes,
                equipmentId: ammoForm.equipmentId ? parseInt(ammoForm.equipmentId, 10) : null
            });
            await fetchAmmoLogs();
            setShowAmmoModal(false);
            resetAmmoForm();
        } catch (err) {
            console.error('Error creating ammo log:', err);
            setError('Failed to create ammunition log.');
        }
    };
    
    // Create service record
    const handleCreateService = async (e) => {
        e.preventDefault();
        try {
            await equipmentService.createEquipmentService({
                equipmentId: parseInt(serviceForm.equipmentId, 10),
                serviceType: serviceForm.serviceType,
                serviceDate: serviceForm.serviceDate,
                provider: serviceForm.provider,
                cost: serviceForm.cost ? parseFloat(serviceForm.cost) : null,
                workDone: serviceForm.workDone,
                nextServiceDue: serviceForm.nextServiceDue || null,
                notes: serviceForm.notes
            });
            await fetchServices();
            setShowServiceModal(false);
            resetServiceForm();
        } catch (err) {
            console.error('Error creating service:', err);
            setError('Failed to create service record.');
        }
    };
    
    // Reset forms
    const resetCertForm = () => {
        setCertForm({
            certificateType: 'SGC',
            certificateNumber: '',
            issuedDate: '',
            expiryDate: '',
            issuingAuthority: '',
            totalSlots: 0,
            notes: ''
        });
    };
    
    const resetAmmoForm = () => {
        setAmmoForm({
            calibre: '',
            transactionType: 'Purchased',
            quantity: 0,
            description: '',
            source: '',
            cost: '',
            transactionDate: new Date().toISOString().split('T')[0],
            notes: '',
            equipmentId: ''
        });
    };
    
    const resetServiceForm = () => {
        setServiceForm({
            equipmentId: '',
            serviceType: 'AnnualService',
            serviceDate: new Date().toISOString().split('T')[0],
            provider: '',
            cost: '',
            workDone: '',
            nextServiceDue: '',
            notes: ''
        });
    };

    const handleDeleteCertificate = async (id) => {
        if (!window.confirm('Delete this certificate record?')) return;
        try {
            await equipmentService.deleteFirearmCertificate(id);
            setCertificates(certificates.filter(c => c.certificateId !== id));
        } catch (err) {
            console.error('Error deleting certificate:', err);
            setError('Failed to delete certificate.');
        }
    };

    const handleDeleteAmmo = async (id) => {
        if (!window.confirm('Delete this ammo log?')) return;
        try {
            await equipmentService.deleteAmmunitionLog(id);
            setAmmoLogs(ammoLogs.filter(a => a.ammunitionLogId !== id));
        } catch (err) {
            console.error('Error deleting ammo log:', err);
            setError('Failed to delete ammo log.');
        }
    };

    const handleDeleteService = async (id) => {
        if (!window.confirm('Delete this service record?')) return;
        try {
            await equipmentService.deleteEquipmentService(id);
            setServices(services.filter(s => s.serviceId !== id));
        } catch (err) {
            console.error('Error deleting service:', err);
            setError('Failed to delete service record.');
        }
    };

    const getExpiryStatus = (expiryDate) => {
        const today = new Date();
        const expiry = new Date(expiryDate);
        const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        
        if (daysUntil < 0) return { status: 'expired', label: 'Expired', color: 'red' };
        if (daysUntil < 30) return { status: 'expiring', label: `Expires in ${daysUntil} days`, color: 'orange' };
        return { status: 'valid', label: `Valid until ${expiry.toLocaleDateString()}`, color: 'green' };
    };

    return (
        <div className="gun-cabinet-page">
            {/* Header */}
            <div className="gun-cabinet-header">
                <div className="gun-cabinet-header-content">
                    <div className="gun-cabinet-header-icon">
                        <Lock size={28} />
                    </div>
                    <div className="gun-cabinet-header-text">
                        <h1>Gun Cabinet</h1>
                        <p>Manage your firearms, certificates, ammunition, and maintenance</p>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && <div className="gun-cabinet-error-message">{error}</div>}

            {/* Tabs and Add Button */}
            <div className="gun-cabinet-tabs-container">
                <div className="gun-cabinet-tabs">
                    <button
                        className={`gun-cabinet-tab ${activeTab === 'certificates' ? 'active' : ''}`}
                        onClick={() => setActiveTab('certificates')}
                    >
                        <Lock size={18} />
                        Certificates (SGC/FAC)
                    </button>
                    <button
                        className={`gun-cabinet-tab ${activeTab === 'ammo' ? 'active' : ''}`}
                        onClick={() => setActiveTab('ammo')}
                    >
                        <Droplet size={18} />
                        Ammunition Log
                    </button>
                    <button
                        className={`gun-cabinet-tab ${activeTab === 'services' ? 'active' : ''}`}
                        onClick={() => setActiveTab('services')}
                    >
                        <Calendar size={18} />
                        Maintenance
                    </button>
                </div>
                <button 
                    className="gun-cabinet-add-btn"
                    onClick={() => {
                        if (activeTab === 'certificates') setShowCertModal(true);
                        else if (activeTab === 'ammo') setShowAmmoModal(true);
                        else if (activeTab === 'services') setShowServiceModal(true);
                    }}
                >
                    <Plus size={18} />
                    Add {activeTab === 'certificates' ? 'Certificate' : activeTab === 'ammo' ? 'Ammo Log' : 'Service'}
                </button>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="gun-cabinet-loading">
                    <div className="gun-cabinet-spinner"></div>
                    <p>Loading gun cabinet data...</p>
                </div>
            )}

            {/* Certificates Tab */}
            {!isLoading && activeTab === 'certificates' && (
                <div className="gun-cabinet-content">
                    {certificates.length > 0 ? (
                        <div className="gun-cabinet-grid">
                            {certificates.map(cert => {
                                const expiry = getExpiryStatus(cert.expiryDate);
                                return (
                                    <div key={cert.certificateId} className={`gun-cabinet-card ${expiry.status}`}>
                                        <div className="gun-cabinet-card-header">
                                            <h3>{cert.certificateType} Certificate</h3>
                                            <span className={`cert-status ${expiry.status}`}>{cert.certificateType}</span>
                                        </div>
                                        <div className="gun-cabinet-card-body">
                                            <p><strong>Number:</strong> {cert.certificateNumber}</p>
                                            <p><strong>Issued:</strong> {new Date(cert.issuedDate).toLocaleDateString()}</p>
                                            <p><strong>Expires:</strong> {new Date(cert.expiryDate).toLocaleDateString()}</p>
                                            <p><strong>Issued By:</strong> {cert.issuingAuthority}</p>
                                            <p><strong>Slots Used:</strong> {cert.slotsUsed}/{cert.totalSlots}</p>
                                        </div>
                                        <div className={`gun-cabinet-expiry-banner ${expiry.status}`}>
                                            {expiry.status === 'expired' && <AlertCircle size={16} />}
                                            <span>{expiry.label}</span>
                                        </div>
                                        <div className="gun-cabinet-card-footer">
                                            <button
                                                className="gun-cabinet-delete-btn"
                                                onClick={() => handleDeleteCertificate(cert.certificateId)}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="gun-cabinet-empty">
                            <Lock size={48} />
                            <h3>No Certificates Registered</h3>
                            <p>Add your SGC (Shotgun Certificate) or FAC (Firearms Certificate) to track expiry dates</p>
                        </div>
                    )}
                </div>
            )}

            {/* Ammunition Tab */}
            {!isLoading && activeTab === 'ammo' && (
                <div className="gun-cabinet-content">
                    {ammoLogs.length > 0 ? (
                        <div className="gun-cabinet-grid">
                            {ammoLogs.map(ammo => (
                                <div key={ammo.ammunitionLogId} className="gun-cabinet-card">
                                    <div className="gun-cabinet-card-header">
                                        <h3>{ammo.calibre}</h3>
                                        <span className="ammo-type">{ammo.ammunitionType}</span>
                                    </div>
                                    <div className="gun-cabinet-card-body">
                                        <p><strong>Equipment:</strong> {ammo.equipmentName || 'N/A'}</p>
                                        <p><strong>Quantity:</strong> {ammo.quantity} rounds</p>
                                        <p><strong>Cost:</strong> £{ammo.costPerRound?.toFixed(2) || 'N/A'}</p>
                                        <p><strong>Manufacturer:</strong> {ammo.manufacturer}</p>
                                        <p><strong>Purchase Date:</strong> {new Date(ammo.purchaseDate).toLocaleDateString()}</p>
                                    </div>
                                    <div className="gun-cabinet-card-footer">
                                        <button
                                            className="gun-cabinet-delete-btn"
                                            onClick={() => handleDeleteAmmo(ammo.ammunitionLogId)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="gun-cabinet-empty">
                            <Droplet size={48} />
                            <h3>No Ammunition Logged</h3>
                            <p>Track your ammunition inventory and costs</p>
                        </div>
                    )}
                </div>
            )}

            {/* Services Tab */}
            {!isLoading && activeTab === 'services' && (
                <div className="gun-cabinet-content">
                    {services.length > 0 ? (
                        <div className="gun-cabinet-grid">
                            {services.map(service => {
                                const today = new Date();
                                const nextService = service.nextServiceDue ? new Date(service.nextServiceDue) : null;
                                const isDue = nextService && nextService <= today;
                                
                                return (
                                    <div key={service.serviceId} className={`gun-cabinet-card ${isDue ? 'service-due' : ''}`}>
                                        <div className="gun-cabinet-card-header">
                                            <h3>{service.equipmentName || 'Equipment'}</h3>
                                            <span className="service-type">{service.serviceType}</span>
                                        </div>
                                        <div className="gun-cabinet-card-body">
                                            <p><strong>Service Date:</strong> {new Date(service.serviceDate).toLocaleDateString()}</p>
                                            {nextService && <p><strong>Next Service:</strong> {nextService.toLocaleDateString()}</p>}
                                            <p><strong>Provider:</strong> {service.provider || 'N/A'}</p>
                                            <p><strong>Cost:</strong> £{service.cost?.toFixed(2) || 'N/A'}</p>
                                            {service.workDone && <p><strong>Work Done:</strong> {service.workDone}</p>}
                                        </div>
                                        {isDue && (
                                            <div className="gun-cabinet-service-banner">
                                                <AlertCircle size={16} />
                                                <span>Service is due!</span>
                                            </div>
                                        )}
                                        <div className="gun-cabinet-card-footer">
                                            <button
                                                className="gun-cabinet-delete-btn"
                                                onClick={() => handleDeleteService(service.serviceId)}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="gun-cabinet-empty">
                            <Calendar size={48} />
                            <h3>No Services Logged</h3>
                            <p>Track your firearm and equipment maintenance schedule</p>
                            <button className="gun-cabinet-empty-btn" onClick={() => setShowServiceModal(true)}>
                                <Plus size={18} />
                                Add Service Record
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Certificate Modal */}
            {showCertModal && (
                <div className="gun-cabinet-modal-overlay" onClick={() => setShowCertModal(false)}>
                    <div className="gun-cabinet-modal" onClick={e => e.stopPropagation()}>
                        <div className="gun-cabinet-modal-header">
                            <h2>Add Certificate</h2>
                            <button className="gun-cabinet-modal-close" onClick={() => setShowCertModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateCertificate}>
                            <div className="gun-cabinet-form-group">
                                <label>Certificate Type</label>
                                <select 
                                    value={certForm.certificateType} 
                                    onChange={e => setCertForm({...certForm, certificateType: e.target.value})}
                                    required
                                >
                                    <option value="SGC">Shotgun Certificate (SGC)</option>
                                    <option value="FAC">Firearms Certificate (FAC)</option>
                                </select>
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Certificate Number</label>
                                <input 
                                    type="text" 
                                    value={certForm.certificateNumber}
                                    onChange={e => setCertForm({...certForm, certificateNumber: e.target.value})}
                                    placeholder="Enter certificate number"
                                    required
                                />
                            </div>
                            <div className="gun-cabinet-form-row">
                                <div className="gun-cabinet-form-group">
                                    <label>Issue Date</label>
                                    <input 
                                        type="date" 
                                        value={certForm.issuedDate}
                                        onChange={e => setCertForm({...certForm, issuedDate: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="gun-cabinet-form-group">
                                    <label>Expiry Date</label>
                                    <input 
                                        type="date" 
                                        value={certForm.expiryDate}
                                        onChange={e => setCertForm({...certForm, expiryDate: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Issuing Constabulary</label>
                                <input 
                                    type="text" 
                                    value={certForm.issuingAuthority}
                                    onChange={e => setCertForm({...certForm, issuingAuthority: e.target.value})}
                                    placeholder="e.g. West Yorkshire Police"
                                    required
                                />
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Slots Granted</label>
                                <input 
                                    type="number" 
                                    value={certForm.totalSlots}
                                    onChange={e => setCertForm({...certForm, totalSlots: e.target.value})}
                                    min="0"
                                />
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Notes</label>
                                <textarea 
                                    value={certForm.notes}
                                    onChange={e => setCertForm({...certForm, notes: e.target.value})}
                                    placeholder="Any additional notes..."
                                    rows={3}
                                />
                            </div>
                            <div className="gun-cabinet-modal-actions">
                                <button type="button" className="gun-cabinet-btn-secondary" onClick={() => setShowCertModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="gun-cabinet-btn-primary">
                                    Add Certificate
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Ammunition Modal */}
            {showAmmoModal && (
                <div className="gun-cabinet-modal-overlay" onClick={() => setShowAmmoModal(false)}>
                    <div className="gun-cabinet-modal" onClick={e => e.stopPropagation()}>
                        <div className="gun-cabinet-modal-header">
                            <h2>Add Ammunition Log</h2>
                            <button className="gun-cabinet-modal-close" onClick={() => setShowAmmoModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateAmmoLog}>
                            <div className="gun-cabinet-form-row">
                                <div className="gun-cabinet-form-group">
                                    <label>Transaction Type</label>
                                    <select 
                                        value={ammoForm.transactionType} 
                                        onChange={e => setAmmoForm({...ammoForm, transactionType: e.target.value})}
                                        required
                                    >
                                        <option value="Purchased">Purchased</option>
                                        <option value="Used">Used</option>
                                        <option value="Transferred">Transferred In</option>
                                        <option value="Sold">Sold</option>
                                        <option value="Reload">Reload</option>
                                    </select>
                                </div>
                                <div className="gun-cabinet-form-group">
                                    <label>Quantity</label>
                                    <input 
                                        type="number" 
                                        value={ammoForm.quantity}
                                        onChange={e => setAmmoForm({...ammoForm, quantity: e.target.value})}
                                        min="1"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Calibre</label>
                                <input 
                                    type="text" 
                                    value={ammoForm.calibre}
                                    onChange={e => setAmmoForm({...ammoForm, calibre: e.target.value})}
                                    placeholder="e.g. .308 Win, 12 Gauge"
                                    required
                                />
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Description</label>
                                <input 
                                    type="text" 
                                    value={ammoForm.description}
                                    onChange={e => setAmmoForm({...ammoForm, description: e.target.value})}
                                    placeholder="e.g. Federal Gold Medal 168gr"
                                />
                            </div>
                            <div className="gun-cabinet-form-row">
                                <div className="gun-cabinet-form-group">
                                    <label>Source/Supplier</label>
                                    <input 
                                        type="text" 
                                        value={ammoForm.source}
                                        onChange={e => setAmmoForm({...ammoForm, source: e.target.value})}
                                        placeholder="Where purchased from"
                                    />
                                </div>
                                <div className="gun-cabinet-form-group">
                                    <label>Cost (Total)</label>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        value={ammoForm.cost}
                                        onChange={e => setAmmoForm({...ammoForm, cost: e.target.value})}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Transaction Date</label>
                                <input 
                                    type="date" 
                                    value={ammoForm.transactionDate}
                                    onChange={e => setAmmoForm({...ammoForm, transactionDate: e.target.value})}
                                    required
                                />
                            </div>
                            {equipment.length > 0 && (
                                <div className="gun-cabinet-form-group">
                                    <label>Link to Firearm (Optional)</label>
                                    <select 
                                        value={ammoForm.equipmentId} 
                                        onChange={e => setAmmoForm({...ammoForm, equipmentId: e.target.value})}
                                    >
                                        <option value="">-- Select Firearm --</option>
                                        {equipment.map(eq => (
                                            <option key={eq.equipmentId} value={eq.equipmentId}>
                                                {eq.brandName} {eq.model}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="gun-cabinet-form-group">
                                <label>Notes</label>
                                <textarea 
                                    value={ammoForm.notes}
                                    onChange={e => setAmmoForm({...ammoForm, notes: e.target.value})}
                                    placeholder="Any additional notes..."
                                    rows={3}
                                />
                            </div>
                            <div className="gun-cabinet-modal-actions">
                                <button type="button" className="gun-cabinet-btn-secondary" onClick={() => setShowAmmoModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="gun-cabinet-btn-primary">
                                    Add Log
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Service Modal */}
            {showServiceModal && (
                <div className="gun-cabinet-modal-overlay" onClick={() => setShowServiceModal(false)}>
                    <div className="gun-cabinet-modal" onClick={e => e.stopPropagation()}>
                        <div className="gun-cabinet-modal-header">
                            <h2>Add Service Record</h2>
                            <button className="gun-cabinet-modal-close" onClick={() => setShowServiceModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateService}>
                            <div className="gun-cabinet-form-group">
                                <label>Equipment</label>
                                <select 
                                    value={serviceForm.equipmentId} 
                                    onChange={e => setServiceForm({...serviceForm, equipmentId: e.target.value})}
                                    required
                                >
                                    <option value="">-- Select Equipment --</option>
                                    {equipment.map(eq => (
                                        <option key={eq.equipmentId} value={eq.equipmentId}>
                                            {eq.brandName} {eq.model}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="gun-cabinet-form-row">
                                <div className="gun-cabinet-form-group">
                                    <label>Service Type</label>
                                    <select 
                                        value={serviceForm.serviceType} 
                                        onChange={e => setServiceForm({...serviceForm, serviceType: e.target.value})}
                                        required
                                    >
                                        <option value="AnnualService">Annual Service</option>
                                        <option value="Repair">Repair</option>
                                        <option value="Clean">Clean</option>
                                        <option value="Modification">Modification</option>
                                        <option value="Inspection">Inspection</option>
                                    </select>
                                </div>
                                <div className="gun-cabinet-form-group">
                                    <label>Service Date</label>
                                    <input 
                                        type="date" 
                                        value={serviceForm.serviceDate}
                                        onChange={e => setServiceForm({...serviceForm, serviceDate: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="gun-cabinet-form-row">
                                <div className="gun-cabinet-form-group">
                                    <label>Service Provider</label>
                                    <input 
                                        type="text" 
                                        value={serviceForm.provider}
                                        onChange={e => setServiceForm({...serviceForm, provider: e.target.value})}
                                        placeholder="Gunsmith/shop name"
                                    />
                                </div>
                                <div className="gun-cabinet-form-group">
                                    <label>Cost</label>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        value={serviceForm.cost}
                                        onChange={e => setServiceForm({...serviceForm, cost: e.target.value})}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Work Done</label>
                                <textarea 
                                    value={serviceForm.workDone}
                                    onChange={e => setServiceForm({...serviceForm, workDone: e.target.value})}
                                    placeholder="Description of work performed..."
                                    rows={3}
                                />
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Next Service Due (Optional)</label>
                                <input 
                                    type="date" 
                                    value={serviceForm.nextServiceDue}
                                    onChange={e => setServiceForm({...serviceForm, nextServiceDue: e.target.value})}
                                />
                            </div>
                            <div className="gun-cabinet-form-group">
                                <label>Notes</label>
                                <textarea 
                                    value={serviceForm.notes}
                                    onChange={e => setServiceForm({...serviceForm, notes: e.target.value})}
                                    placeholder="Any additional notes..."
                                    rows={2}
                                />
                            </div>
                            <div className="gun-cabinet-modal-actions">
                                <button type="button" className="gun-cabinet-btn-secondary" onClick={() => setShowServiceModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="gun-cabinet-btn-primary">
                                    Add Service
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GunCabinet;
